<template>
  <div>
    <v-dialog v-model="showDeleteConfirmationDialog" max-width="550px">
      <v-card>
        <v-card-title>
          <h3>Are you sure you want to delete show result?</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="showDeleteConfirmationDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="confirmDelete"> Delete </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-alert width="100%" v-if="infoMessage" type="info" dismissible>
      {{ infoMessage }}</v-alert>
    <v-alert width="100%" v-if="errorMessage" type="error" dismissible>
      {{ errorMessage }}</v-alert>
    <table class="pl-3 pr-3">
      <tr>
        <th>Titled Dog</th>
        <th>Show Date</th>
        <th>Title Earned</th>
        <th></th>
        <!-- actions column -->
      </tr>
      <tr id="sr-empty" v-if="!!emptyRow" class="orange lighten-4">
        <td class="pr-1" :style="`text-align: left; width: 300px`">
          <AdminDogSearchDialog inputId="srEmptyDogId" v-bind:dogId.sync="emptyRow.dogId" :isEditMode="true" />
        </td>
        <td class="pr-1 pl-1">
          <DatepickerFormatted :disableClearable="true" :date="emptyRow.showDate" v-on:updateDates="
            ($event) => updateDates($event, 'showDate', emptyRow)
          " />
        </td>
        <td class="pr-1 pl-1">
          <v-autocomplete v-model="emptyRow.categoryId" item-value="title" item-text="description" :items="titles"
            :rules="[(v) => !!v || 'Required']" v-on:change="updateShowDate" />
        </td>
        <td class="pl-1" style="width: 60px">
          <v-icon class="mr-1" color="primary" @click="saveShowResult(emptyRow)">
            mdi-content-save-edit
          </v-icon>
        </td>
      </tr>
    </table>
    <v-divider inset />
    <v-divider inset />
    <v-virtual-scroll :items="showResults" :item-height="100" height="680" content-tag="table"
      id="JuniorTitledDogsVirtualScroller">
      <template v-slot:default="{ item, index }">
        <v-list-item key="item.id" :id="`sr-${item.id}`">
          <v-list-item-content>
            <tr :class="row_classes(item, index)" :id="item.id">
              <td class="pr-1" :style="`text-align: left; width: 300px`">
                <AdminDogSearchDialog v-bind:dogId.sync="item.dogId" :isEditMode="true" />
              </td>
              <td class="pr-1 pl-1">
                <DatepickerFormatted :disableClearable="true" :date="item.showDate" v-on:updateDates="
                  ($event) => updateDates($event, 'showDate', item)
                " />
              </td>
              <td class="pr-1 pl-1">
                <v-autocomplete v-model="item.categoryId" item-value="title" item-text="description"
                  :items="item.dogCerts" :rules="[(v) => !!v || 'Required']" v-on:change="(v) => updateShowDateByDogId(item.dogId, v)"/>
              </td>
              <td class="pl-1" style="width: 60px">
                <v-icon class="mr-1" color="primary" @click="saveShowResult(item)">
                  mdi-content-save-edit
                </v-icon>
                <v-icon :disabled="!item.id" tabindex="-1" @click="deleteShowResult(item)">
                  mdi-delete
                </v-icon>
              </td>
            </tr>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import * as _ from "lodash";
import { find } from "lodash";

import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";
import AdminDogSearchDialog from "@/components/Admin/AdminDogSearchDialog.vue";

import dogService from "@/services/admin/dogService";
import juniorHandlerService from "@/services/admin/juniorHandlerService";

import { TitledDogs } from "@/types/admin/JuniorHandler";

export default Vue.extend({
  name: "TitledDogs",
  props: {
    personId: { type: Number },
    titlesTypes: { type: [] as any[] },
  },
  components: {
    DatepickerFormatted,
    AdminDogSearchDialog,
  },
  data: function () {
    return {
      emptyRow: {} as TitledDogs,
      showDeleteConfirmationDialog: false,
      deletedItem: null as any,
      showResults: [] as TitledDogs[],
      persistedShowResults: [] as TitledDogs[],
      errorMessage: "" as string,
      infoMessage: "" as string,
      titles: [] as any[]
    };
  },
  created: function () {
    this.emptyRow = Object.assign({}, {} as TitledDogs);
    if (this.personId) {
      this.reloadData();
    }
  },
  watch: {
    personId: function (newVal: String) {
      this.reloadData();
    },
    emptyRow: {
      deep: true,
      handler(val, oldVal) {
        if (!val.dogId) return;
        dogService.getDogById(val.dogId)
          .then((res) => {
            this.titles = res.dogCerts
            this.showResults = this.showResults.map(r => r.dogId === val.dogId ? {...r, dogCerts: res.dogCerts }: r);
          })
          .catch(() => {
            this.errorMessage = "Error with loading dog information."
          })
      },
    }
  },
  methods: {
    updateShowDate: function (v: string) {
      let cert = find(this.titles, t => t.title === v);
      if (!cert) return;
      this.emptyRow.showDate = cert.showDate;
    },
    updateShowDateByDogId: function (dogId: string, certTitle: string) {
      const result = find(this.showResults, r => r.dogId === dogId);
      if (!result) return;
      let cert = find((result as any).dogCerts, t => t.title === certTitle);
      if (!cert) return;    
      this.showResults = this.showResults.map(r => r.dogId === dogId ? {...r, showDate: cert.showDate } : r)
    },
    reloadData: function () {
      juniorHandlerService
        .getTitledDogsByJuniorPersonId(this.personId)
        .then((res) => {
          this.showResults = res;
          this.persistedShowResults = _.cloneDeep(res);
          this.emptyRow = this.emptyShowResultRow();
        });
    },
    emptyShowResultRow: function () {
      return {
        id: null,
        dogId: '',
        dogName: '',
        showDate: null,
        categoryId: '',
        juniorPersonId: this.personId
      }
    },
    updateDates: function (val: any, key: string, item: any) {
      item[key] = val?.date;
      //workaround to force rerendering after setting up formatted date
      this.$set(this, "showResults", this.showResults);
    },
    row_classes: function (item: any, index: number) {
      if (!!item.id) {
        const index = this.showResults.findIndex((i) => i.id === item.id);
        if (
          _.isEqual(this.showResults[index], this.persistedShowResults[index])
        ) {
          return "";
        }
      }
      return "orange  lighten-4";
    },
    saveShowResult: function (item: TitledDogs) {
      this.$store.dispatch("changeLoaderStatus", true);
      const isAddingNewRow = !item.id;

      juniorHandlerService.updateTitledDogEntry(item)
        .then((res: any) => {
          if (!res.success) {
            this.errorMessage = res.messages.join(", ");
            if (isAddingNewRow) {
              const input = document.querySelector('#srEmptyDogId') as HTMLElement | null;

              if (input != null) {
                input.focus();
              }
            }
          } else {
            if (isAddingNewRow) {
              this.emptyRow.id = res.id;
              this.showResults.unshift({...this.emptyRow, dogCerts: this.titles } as any);
              this.persistedShowResults = _.cloneDeep(this.showResults);

              this.emptyRow = Object.assign({}, this.emptyShowResultRow());
              this.$set(this, "emptyRow", this.emptyRow);

              const input = document.querySelector('#srEmptyDogId') as HTMLElement | null;

              if (input != null) {
                input.focus();
              }
            } else {
              this.persistedShowResults = _.cloneDeep(this.showResults);
            }
            this.errorMessage = "";
          }
        })
        .catch((e) => {
          this.errorMessage = "Changes couldn't be saved. Ensure there are no validation errors before retrying.";
        })
        .finally(() => { this.$store.dispatch("changeLoaderStatus", false); });
    },
    deleteShowResult: function (item: TitledDogs) {
      if (item.id) {
        //if that item has been persisted already
        this.deletedItem = item;
        this.showDeleteConfirmationDialog = true;
      }
    },
    confirmDelete: function () {
      if (this.deletedItem == null) {
        return;
      }
      juniorHandlerService.deleteTitledDogEntry(this.deletedItem.id)
        .then((res: any) => {
          if (!res.success) {
            this.errorMessage = res.messages.join(", ");
          } else {
            this.deletedItem = null;
            this.reloadData();
          }
        })
        .finally(() => { this.showDeleteConfirmationDialog = false });
    },
  },
});
</script>
